import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { Icon, IconName } from '../../utils/icons';
import { useThemeStore } from '../../store/themeStore';
import { getDesignTokens } from '../../utils/designTokens';
import { useCurrentUser } from '../../hooks/useCurrentUser';

interface NavItem {
  path: string;
  label: string;
  icon: IconName;
  isTodayNav?: boolean;
  isAvatar?: boolean;
}

const navItems: NavItem[] = [
  { path: '/mobile/today', label: 'Today', icon: 'home', isTodayNav: true },
  { path: '/mobile/calendar', label: 'Calendar', icon: 'calendar' },
  { path: '/mobile/discoveries', label: 'Discoveries', icon: 'discoveries' },
  { path: '/mobile/todos', label: 'To Dos', icon: 'checklist' },
  { path: '/mobile/create', label: 'Create', icon: 'plus' },
  { path: '/mobile/me', label: 'Account', icon: 'settings', isAvatar: true },
];

export default function BottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode } = useThemeStore();
  const tokens = getDesignTokens(isDarkMode);
  const { avatar, initials, color } = useCurrentUser();
  const [activeIndex, setActiveIndex] = useState(0);
  const [sliderStyle, setSliderStyle] = useState({ left: '0%', width: `${100 / navItems.length}%` });
  const navRef = useRef<HTMLDivElement>(null);

  const isActive = (item: NavItem): boolean => {
    if (item.isTodayNav) {
      return location.pathname === '/mobile/today';
    }
    return location.pathname.startsWith(item.path);
  };

  // Calculate active index and slider position
  useEffect(() => {
    const activeIdx = navItems.findIndex(item => isActive(item));
    if (activeIdx !== -1 && activeIdx !== activeIndex) {
      setActiveIndex(activeIdx);
      const itemWidth = 100 / navItems.length;
      setSliderStyle({
        left: `${activeIdx * itemWidth}%`,
        width: `${itemWidth}%`
      });
    }
  }, [location.pathname, location.state]);

  return (
    <nav 
      className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-gray-300 z-50 shadow-lg"
      style={{ 
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        height: 'calc(56px + env(safe-area-inset-bottom, 0px))'
      }}
    >
      <div ref={navRef} className="flex items-center justify-around h-14 relative">
        {/* Sliding Background Box */}
        <div 
          className={`absolute ${tokens.button.primary} rounded-landing transition-all duration-300 ease-in-out`}
          style={{
            ...sliderStyle,
            top: '8px',
            bottom: '8px',
            zIndex: 0
          }}
        />
        
        {navItems.map((item) => {
          const active = isActive(item);
          const handleClick = () => {
            navigate(item.path);
          };
          return (
            <button
              key={item.path}
              onClick={handleClick}
              className="flex flex-col items-center justify-center flex-1 min-w-0 px-0.5 py-1 touch-manipulation relative z-10"
              aria-label={item.label}
              style={{ minHeight: '48px' }}
            >
              <div className="flex flex-col items-center justify-center w-full">
                {/* Fixed-size icon area; avatar gets mt-2 to sit at bar vertical center */}
                <div className={`flex-shrink-0 w-10 h-10 flex items-center justify-center ${item.isAvatar ? 'mt-2' : ''}`}>
                  {item.isAvatar ? (
                    <div className="relative w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-md overflow-hidden box-border" style={{ backgroundColor: color }}>
                      {!avatar && initials}
                      {avatar && (
                        <img
                          src={avatar}
                          alt={item.label}
                          className="absolute inset-0 w-full h-full rounded-full object-cover box-border"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      )}
                    </div>
                  ) : (
                    <Icon 
                      name={item.icon} 
                      size="md" 
                      solid={active}
                      className={active ? tokens.text.inverse : 'text-gray-500'}
                    />
                  )}
                </div>
                {!item.isAvatar && (
                  <span 
                    className={`text-[10px] font-medium mt-0.5 leading-tight text-center transition-colors ${
                      active ? tokens.text.inverse : 'text-gray-500'
                    }`}
                    style={{ 
                      fontSize: '10px',
                      lineHeight: '1.2',
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {item.label}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
